import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCorners,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Plus, Settings2, Loader2, Trash2, FileText } from 'lucide-react';
import DragDotsIcon from '../components/icons/DragDotsIcon';
import { Lead, STATUS_CONFIG } from '../types/lead';
import { Stage } from '../types/stage';
import { Tag } from '../types/tag';
import { updateLead } from '../lib/leadsManagement';
import { createStage, deleteStage } from '../lib/stagesManagement';
import PageHeader from '../components/Layout/PageHeader';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';

interface PipelinePageProps {
  leads: Lead[];
  stages: Stage[];
  leadTagsMap: Map<string, Tag[]>;
  leadNotesMap: Map<string, string>;
  onLeadClick: (lead: Lead) => void;
  onStagesChange: (stages: Stage[]) => void;
  onLeadsChange: (leads: Lead[]) => void;
}

// Draggable Lead Card
const DraggableLeadCard: React.FC<{
  lead: Lead;
  tags: Tag[];
  latestNote?: string;
  onClick: () => void;
  isDragging?: boolean;
}> = ({ lead, tags, latestNote, onClick, isDragging }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging: isSortDragging } = useSortable({ id: lead.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortDragging ? 0.3 : 1,
  };

  const cfg = STATUS_CONFIG[lead.status];
  const company = lead.company || lead.meta_data?.company;


  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`bg-white border border-zinc-200 rounded-xl p-3.5 hover:shadow-md hover:border-zinc-300 transition-all group ${isDragging ? 'shadow-xl rotate-1 border-zinc-900' : ''}`}
    >
      <div className="flex items-start gap-2">
        <div
          {...listeners}
          style={{ touchAction: 'none' }}
          className="mt-0.5 text-zinc-400 flex-shrink-0 cursor-grab active:cursor-grabbing p-1 -m-1"
        >
          <DragDotsIcon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0" onClick={e => { e.stopPropagation(); if (!isSortDragging) onClick(); }}>
          <p className="text-sm font-semibold text-zinc-900 truncate">{lead.name}</p>
          {company && <p className="text-xs text-zinc-400 truncate">{company}</p>}

          {/* Letzte Notiz */}
          {latestNote && (
            <div className="flex items-start gap-1.5 mt-2 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5">
              <FileText className="w-3 h-3 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-zinc-600 leading-relaxed line-clamp-2">{latestNote}</p>
            </div>
          )}

          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
            {tags.slice(0, 2).map(tag => (
              <span
                key={tag.id}
                className="text-xs px-1.5 py-0.5 rounded-full font-medium"
                style={{ backgroundColor: tag.color + '20', color: tag.color }}
              >
                {tag.name}
              </span>
            ))}
          </div>
        </div>
        <div
          {...listeners}
          style={{ touchAction: 'none' }}
          className="mt-0.5 text-zinc-400 flex-shrink-0 cursor-grab active:cursor-grabbing p-1 -m-1"
        >
          <DragDotsIcon className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
};

// Stage Column
const StageColumn: React.FC<{
  stage: Stage;
  leads: Lead[];
  tags: Map<string, Tag[]>;
  notesMap: Map<string, string>;
  onLeadClick: (lead: Lead) => void;
  onDeleteStage: (id: string) => void;
}> = ({ stage, leads, tags, notesMap, onLeadClick, onDeleteStage }) => {
  const { setNodeRef: setDropRef } = useDroppable({ id: `column-${stage.id}` });
  const { isKunde } = useAuth();
  return (
    <div ref={setDropRef} className="flex-shrink-0 w-72 flex flex-col gap-3">
      <div className="flex items-center justify-between px-3 py-2.5 bg-zinc-900 rounded-xl">
        <div className="flex items-center gap-2">
          <span className="font-bold text-white text-sm uppercase tracking-widest">{stage.name}</span>
          <span className="text-xs text-zinc-400 bg-zinc-800 px-2 py-0.5 rounded-full">{leads.length}</span>
        </div>
        {!isKunde() && (
          <button onClick={() => onDeleteStage(stage.id)} className="text-zinc-600 hover:text-red-400 transition-colors p-1">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <SortableContext items={leads.map(l => l.id)} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col gap-2.5 min-h-[120px]">
          <AnimatePresence>
            {leads.map(lead => (
              <motion.div key={lead.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}>
                <DraggableLeadCard
                  lead={lead}
                  tags={tags.get(lead.id) || []}
                  latestNote={notesMap.get(lead.id)}
                  onClick={() => onLeadClick(lead)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
          {leads.length === 0 && (
            <div className="border-2 border-dashed border-zinc-200 rounded-xl h-20 flex items-center justify-center">
              <p className="text-xs text-zinc-400">Anfrage hierher ziehen</p>
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
};

const PipelinePage: React.FC<PipelinePageProps> = ({
  leads,
  stages,
  leadTagsMap,
  leadNotesMap,
  onLeadClick,
  onStagesChange,
  onLeadsChange,
}) => {
  const [activeLeadId, setActiveLeadId] = useState<string | null>(null);
  const [newStageName, setNewStageName] = useState('');
  const [addingStage, setAddingStage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteStageId, setConfirmDeleteStageId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const pipelineLeads = useMemo(() => leads.filter(l => l.stage_id && !l.archived), [leads]);

  const leadsByStage = useMemo(() => {
    const map = new Map<string, Lead[]>();
    stages.forEach(s => map.set(s.id, []));
    pipelineLeads.forEach(lead => {
      if (lead.stage_id && map.has(lead.stage_id)) {
        map.get(lead.stage_id)!.push(lead);
      }
    });
    return map;
  }, [stages, pipelineLeads]);

  const activeLead = activeLeadId ? leads.find(l => l.id === activeLeadId) : null;

  const handleDragStart = (e: DragStartEvent) => setActiveLeadId(String(e.active.id));

  const handleDragEnd = async (e: DragEndEvent) => {
    const { active, over } = e;
    setActiveLeadId(null);
    if (!over) return;

    const leadId = String(active.id);
    const overId = String(over.id);

    let targetStageId: string | null = null;
    if (overId.startsWith('column-')) {
      targetStageId = overId.replace('column-', '');
    } else {
      const overLead = leads.find(l => l.id === overId);
      targetStageId = overLead?.stage_id || null;
    }

    if (!targetStageId) return;
    const lead = leads.find(l => l.id === leadId);
    if (!lead || lead.stage_id === targetStageId) return;

    onLeadsChange(leads.map(l => l.id === leadId ? { ...l, stage_id: targetStageId } : l));
    await updateLead(leadId, { stage_id: targetStageId });
  };

  const handleAddStage = async () => {
    if (!newStageName.trim() || saving) return;
    setSaving(true);
    const stage = await createStage(newStageName.trim(), stages.length);
    if (stage) {
      onStagesChange([...stages, stage]);
      setNewStageName('');
      setAddingStage(false);
    }
    setSaving(false);
  };

  const handleDeleteStage = async (stageId: string) => {
    await deleteStage(stageId);
    onStagesChange(stages.filter(s => s.id !== stageId));
    onLeadsChange(leads.map(l => l.stage_id === stageId ? { ...l, stage_id: null } : l));
    setConfirmDeleteStageId(null);
  };

  const unassignedCount = leads.filter(l => !l.stage_id && !l.archived).length;
  const { isKunde } = useAuth();

  return (
    <div>
      <PageHeader
        title="Pipeline"
        subtitle={`${pipelineLeads.length} Anfragen · ${unassignedCount} ohne Stufe`}
        actions={!isKunde() ? (
          <button
            onClick={() => setAddingStage(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-900 text-white text-sm font-semibold hover:bg-zinc-800 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Neue Stufe</span>
          </button>
        ) : undefined}
      />

      <div className="px-4 md:px-8 py-4">
        <AnimatePresence>
          {addingStage && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-4 overflow-hidden"
            >
              <div className="flex gap-2 bg-white border border-zinc-200 rounded-xl p-3">
                <input
                  type="text"
                  value={newStageName}
                  onChange={e => setNewStageName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddStage()}
                  placeholder="Name der neuen Stufe..."
                  autoFocus
                  className="flex-1 text-sm focus:outline-none"
                />
                <button onClick={() => setAddingStage(false)} className="px-3 py-1.5 text-sm border border-zinc-200 rounded-lg hover:bg-zinc-50 text-zinc-600">
                  Abbrechen
                </button>
                <button
                  onClick={handleAddStage}
                  disabled={!newStageName.trim() || saving}
                  className="px-4 py-1.5 bg-zinc-900 text-white text-sm font-semibold rounded-lg disabled:opacity-40 hover:bg-zinc-800 transition-colors flex items-center gap-1.5"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Erstellen
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {stages.length === 0 ? (
          <div className="text-center py-20 text-zinc-400">
            <Settings2 className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <p className="font-semibold text-zinc-500">Noch keine Pipeline-Stufen</p>
            <p className="text-sm mt-1">Erstelle deine erste Stufe oben</p>
          </div>
        ) : (
          <div className="overflow-x-auto pb-4 kanban-scroll" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y' }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="flex gap-4" style={{ width: 'max-content', minWidth: '100%' }}>
              {stages.map(stage => (
                <StageColumn
                  key={stage.id}
                  stage={stage}
                  leads={leadsByStage.get(stage.id) || []}
                  tags={leadTagsMap}
                  notesMap={leadNotesMap}
                  onLeadClick={onLeadClick}
                  onDeleteStage={setConfirmDeleteStageId}
                />
              ))}
            </div>

            <DragOverlay>
              {activeLead && (
                <DraggableLeadCard
                  lead={activeLead}
                  tags={leadTagsMap.get(activeLead.id) || []}
                  latestNote={leadNotesMap.get(activeLead.id)}
                  onClick={() => {}}
                  isDragging
                />
              )}
            </DragOverlay>
          </DndContext>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmDeleteStageId !== null}
        title="Stufe löschen?"
        message="Die Stufe wird gelöscht. Alle Anfragen in dieser Stufe verlieren ihre Zuweisung."
        onConfirm={() => confirmDeleteStageId && handleDeleteStage(confirmDeleteStageId)}
        onCancel={() => setConfirmDeleteStageId(null)}
      />
    </div>
  );
};

export default PipelinePage;
